import React from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtCurrency } from '../lib/display-labels';
import {
  Wrench,
  Users,
  DollarSign,
  ClipboardCheck,
  FileText,
  Receipt,
  ReceiptText,
  Settings,
  ChevronRight,
  BarChart3,
  Building2,
  Warehouse,
  CalendarDays,
  Clock,
  ArrowLeft,
  ShieldCheck,
  Handshake,
  TrendingUp,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getCasingMoreItems } from '../config/casingNav';
import { isSalesRole, navPathOpenForRole } from '../lib/sales-access';
import SignOutButton from '../components/SignOutButton';
import { useVerticalPhoneNav, VerticalMoreSections } from '../components/verticalPhoneNav';
import fieldiqIcon from '../assets/fieldiq-icon.png';
import fieldiqIconLight from '../assets/fieldiq-icon-light.png';

// Maps lucide icon names (strings from casingNav.ts) to their components.
const ICON: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Wrench,
  Warehouse,
  Users,
  CalendarDays,
  Clock,
  DollarSign,
  ClipboardCheck,
  FileText,
  Receipt,
  ReceiptText,
  Building2,
  BarChart3,
  ShieldCheck,
  Settings,
  Handshake,
  TrendingUp,
};

// Parse YYYY-MM-DD as a local date to avoid timezone shift (matches PayrollScreen)
function parseUTC(d: string): Date {
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day);
}

function formatPeriodRange(start: string, end: string) {
  try {
    const s = parseUTC(start);
    const e = parseUTC(end);
    const sameMonth = s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();
    return sameMonth
      ? `${format(s, 'MMM d')}-${format(e, 'd')}`
      : `${format(s, 'MMM d')} - ${format(e, 'MMM d')}`;
  } catch { return `${start} - ${end}`; }
}

/** Whole-dollar money. @see fmtCurrency — absence renders '—', not '$0'. */
const moneyShort = (v: string | number | null | undefined) => fmtCurrency(v, null, 0);

export default function MoreScreen() {
  const navigate = useNavigate();
  const { company, user, hasVertical } = useAuth();
  const { theme } = useTheme();
  const isReviewer = ['admin', 'dispatcher', 'supervisor', 'office_staff'].includes(user?.role);
  // The four live subtitles below are equipment, team, pay and rate-sheet reads,
  // all refused to a sales user (api#1032) — and their entries are hidden from
  // them anyway — so they are not fired for that role.
  const fetchLiveSubtitles = !isSalesRole(user?.role);
  const icon = theme === 'light' ? fieldiqIconLight : fieldiqIcon;

  const { data: equipmentData } = useQuery({
    queryKey: ['equipment'],
    enabled: fetchLiveSubtitles,
    queryFn: () => api.equipment.list().then(r => r.data.data ?? r.data ?? []),
  });
  const equipmentCount = (equipmentData ?? []).length;

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    enabled: fetchLiveSubtitles,
    queryFn: () => api.users.list().then(r => r.data.data ?? r.data ?? []),
  });
  const teamCount = (usersData ?? []).length;

  const { data: payPeriods, isLoading: payPeriodsLoading } = useQuery({
    queryKey: ['pay-periods'],
    enabled: fetchLiveSubtitles,
    queryFn: () => api.payroll.listPayPeriods().then(r => r.data.data ?? r.data ?? []),
  });
  const periods = payPeriods ?? [];
  const activePeriod = periods.find((p: any) => parseFloat(String(p.total_gross_pay)) > 0) ?? periods[0];
  const payrollSubtitle = payPeriodsLoading
    ? '—'
    : !activePeriod
      ? 'No pay periods yet'
      : `${formatPeriodRange(activePeriod.period_start, activePeriod.period_end)} · ${moneyShort(activePeriod.total_gross_pay)}`;

  const { data: rateSheetsData } = useQuery({
    queryKey: ['rate-sheets'],
    enabled: fetchLiveSubtitles,
    queryFn: () => api.rateSheets.list().then(r => r.data.data ?? r.data ?? []),
  });
  const activeSheetCount = (rateSheetsData ?? []).filter((s: any) => s.status === 'active').length;
  const rateSheetsSubtitle = `${activeSheetCount} active sheet${activeSheetCount !== 1 ? 's' : ''}`;

  // Resolve a config subtitle sentinel to the live/dynamic value; pass static strings through.
  const resolveSubtitle = (subtitle?: string): string => {
    switch (subtitle) {
      case 'LIVE_EQUIPMENT_COUNT':
        return `${equipmentCount} item${equipmentCount !== 1 ? 's' : ''}`;
      case 'LIVE_TEAM_COUNT':
        return `${teamCount} employee${teamCount !== 1 ? 's' : ''}`;
      case 'LIVE_PAYROLL':
        return payrollSubtitle;
      case 'LIVE_RATE_SHEET_COUNT':
        return rateSheetsSubtitle;
      default:
        return subtitle ?? '';
    }
  };

  // Evaluate a config gate expression against local role flags.
  const passesGate = (gate?: string): boolean => {
    if (!gate) return true;
    if (gate === 'isReviewer') return isReviewer;
    return true;
  };

  // A trucking / rentals tenant (not casing) gets the sidebar's groups here
  // instead of the casing list (#803; see verticalPhoneNav.tsx). Everyone else's
  // More is the list below, unchanged.
  const verticalNav = useVerticalPhoneNav(hasVertical);
  // Same rule as the casing list: a sales user sees only what they may open.
  const moreSections = verticalNav.more
    .map((section) => ({ ...section, items: section.items.filter((it) => navPathOpenForRole(user?.role, it.path)) }))
    .filter((section) => section.items.length > 0);

  const menuItems = getCasingMoreItems()
    .filter((item) => passesGate(item.gate))
    .filter((item) => navPathOpenForRole(user?.role, item.morePath ?? item.path))
    .map((item) => ({
      title: item.moreLabel ?? item.label,
      subtitle: resolveSubtitle(item.moreSubtitle),
      icon: ICON[item.icon],
      path: item.morePath ?? item.path,
      color: 'text-fieldiq-accent',
    }));

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">More</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 pb-[var(--bottom-chrome)] space-y-2">
        {verticalNav.enabled ? <VerticalMoreSections sections={moreSections} onOpen={navigate} /> : menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.title}
              onClick={() => navigate(item.path)}
              className="bg-fieldiq-card rounded-xl p-4 flex items-center justify-between card-hover"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-fieldiq-dark flex items-center justify-center">
                  <Icon size={20} className={item.color} />
                </div>
                <div>
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="text-xs text-fieldiq-text">{item.subtitle}</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-fieldiq-muted" />
            </div>
          );
        })}

        {/* Wrapper (not on the field screen) separates Sign Out from the menu rows.
            The list's space-y-2 outspecifies any mt-* on a direct child, so the
            divider + the button's own mt-4 do the spacing work instead. */}
        <div className="border-t border-fieldiq-border">
          <SignOutButton />
        </div>

        {/* Company Info */}
        <div className="mt-8 p-4 bg-fieldiq-card rounded-xl text-center">
          <img src={icon} alt="FieldIQ" className="w-16 h-16 rounded-xl mx-auto mb-3" />
          <h3 className="font-bold">{company?.displayName || 'FieldIQ'}</h3>
          <p className="text-xs text-fieldiq-muted mt-4">FieldIQ v1.0.0</p>
        </div>

      </div>
    </div>
  );
}
